import React from "react";

/**
 * One row of an OPC UA enum/union reference table shown inside a Tooltip —
 * see `EnumHelpTable`. `note` is optional per row: the table only renders a
 * Note column at all when at least one row supplies one.
 */
export interface EnumHelpRow {
  value: string;
  meaning: string;
  /** Omitted rows render "—" when the table has a Note column. */
  note?: string;
}

const enumTableCellStyle: React.CSSProperties = {
  border: "1px solid rgba(255, 255, 255, 0.25)",
  padding: "4px 8px",
  textAlign: "left",
  verticalAlign: "top",
};

/**
 * Renders an enum/union reference table (Security Mode, Security Policy,
 * Identity, ...) inside a Tooltip. Shared across the OPC UA CDO editors
 * whose fields hold numeric enum values or a `kind` discriminant that give
 * the operator no clue what they mean on their own.
 */
export default function EnumHelpTable({
  valueHeader,
  meaningHeader,
  noteHeader = undefined,
  rows,
}: {
  valueHeader: string;
  meaningHeader: string;
  noteHeader?: string;
  rows: EnumHelpRow[];
}) {
  const hasNotes = rows.some((row) => row.note !== undefined);
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 12, maxWidth: 480 }}>
      <thead>
        <tr>
          <th style={enumTableCellStyle}>{valueHeader}</th>
          <th style={enumTableCellStyle}>{meaningHeader}</th>
          {hasNotes && <th style={enumTableCellStyle}>{noteHeader}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.value}>
            <td style={enumTableCellStyle}>
              <strong>{row.value}</strong>
            </td>
            <td style={enumTableCellStyle}>{row.meaning}</td>
            {hasNotes && <td style={enumTableCellStyle}>{row.note ?? "—"}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
